/*
 * 文件职责 / File Purpose
 * - 中文：根据当前研究路由显示主研究泳道、相邻入口和工作区提示，帮助不同研究页面保持一致的导航语义。
 * - English: Presents primary research lanes, adjacent entries, and workspace cues from the active route so research pages share one navigation language.
 *
 * 数据与交互边界 / Data and Interaction Boundary
 * - 中文：仅消费 researchNavigation 与主题元数据并执行前端跳转；不读取研究数据、不触发研究任务，也不判断模块健康状态。
 * - English: Consumes navigation/theme metadata and performs browser navigation only; it reads no research facts, starts no jobs, and infers no module health.
 */
import React from 'react';
import { history } from '@umijs/max';
import { createStyles } from 'antd-style';
import {
  getPrimaryResearchLanes,
  resolveResearchRouteMeta,
} from '@/utils/researchNavigation';
import {
  buildResearchWorkspaceThemeStyle,
  resolveResearchWorkspaceTheme,
} from '@/utils/researchWorkspaceTheme';

const MINIMAL_WORKSPACE_PATHS = new Set([
  '/dashboard/workplace',
  '/market/overview',
  '/research/symbol',
  '/strategy/library',
  '/strategy/signals',
]);
const HIDDEN_WORKSPACE_GUIDE_PATHS = new Set([
  '/company/analysis',
  '/intelligence/industry-mapping-review',
  '/intelligence/macro',
  '/intelligence/terminal',
  '/intelligence/strategy8-coverage',
  '/intelligence/workbench',
  '/intelligence/finance-mcp',
  '/research/watchlists',
]);

const useStyles = createStyles(() => ({
  wrapper: {
    padding: '16px 24px 0',
    '@media (max-width: 768px)': {
      padding: '12px 12px 0',
    },
  },
  panel: {
    display: 'grid',
    gap: 16,
    padding: 18,
    borderRadius: 22,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    background:
      'var(--research-theme-panel-bg, linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 58%, rgba(30, 64, 175, 0.82) 100%))',
    boxShadow: 'var(--research-theme-shadow, 0 20px 48px rgba(15, 23, 42, 0.16))',
  },
  panelCompact: {
    gap: 0,
    padding: 14,
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 0,
    alignItems: 'start',
    '@media (max-width: 1080px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  headingStack: {
    display: 'grid',
    gap: 10,
  },
  eyebrow: {
    color: 'var(--research-theme-eyebrow, #93c5fd)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  titleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
  },
  title: {
    margin: 0,
    color: '#f8fafc',
    fontSize: 22,
    lineHeight: 1.2,
    fontWeight: 700,
  },
  desc: {
    margin: 0,
    color: 'rgba(226, 232, 240, 0.86)',
    fontSize: 14,
    lineHeight: 1.7,
    maxWidth: 760,
  },
  laneRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  laneButton: {
    padding: '10px 14px',
    borderRadius: 16,
    border: '1px solid rgba(148, 163, 184, 0.16)',
    background: 'rgba(255, 255, 255, 0.04)',
    color: '#cbd5e1',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 180ms ease-out, background 180ms ease-out, border-color 180ms ease-out, color 180ms ease-out',
    ':hover': {
      transform: 'translateY(-1px)',
      color: '#f8fafc',
      borderColor: 'var(--research-theme-hover-border, rgba(96, 165, 250, 0.3))',
      background: 'var(--research-theme-hover-bg, rgba(59, 130, 246, 0.12))',
    },
  },
  laneButtonActive: {
    color: '#f8fafc',
    borderColor: 'var(--research-theme-active-border, rgba(245, 158, 11, 0.3))',
    background: 'var(--research-theme-active-bg, rgba(245, 158, 11, 0.16))',
    boxShadow: 'var(--research-theme-active-shadow, inset 0 0 0 1px rgba(245, 158, 11, 0.1))',
    ':hover': {
      transform: 'none',
      color: '#f8fafc',
      borderColor: 'var(--research-theme-active-border, rgba(245, 158, 11, 0.3))',
      background: 'var(--research-theme-active-bg, rgba(245, 158, 11, 0.16))',
    },
  },
  laneLabel: {
    display: 'block',
  },
  lanePath: {
    display: 'block',
    marginTop: 4,
    color: 'rgba(148, 163, 184, 0.82)',
    fontSize: 11,
    fontWeight: 500,
  },
  '@media (prefers-reduced-motion: reduce)': {
    laneButton: {
      transition: 'none',
    },
  },
}));

const ResearchWorkspaceGuide = ({ location }) => {
  const { styles } = useStyles();
  const pathname = location?.pathname || window.location.pathname;
  const search = location?.search || window.location.search;
  const routeMeta = resolveResearchRouteMeta(pathname, search);
  const workspaceTheme = resolveResearchWorkspaceTheme(pathname);
  const workspaceThemeStyle = buildResearchWorkspaceThemeStyle(workspaceTheme);

  if (!routeMeta || HIDDEN_WORKSPACE_GUIDE_PATHS.has(pathname)) {
    return null;
  }

  const primaryLanes = getPrimaryResearchLanes();
  const isMinimalWorkspace = MINIMAL_WORKSPACE_PATHS.has(pathname);

  return (
    <div className={styles.wrapper}>
      <section
        className={`${styles.panel} ${isMinimalWorkspace ? styles.panelCompact : ''}`}
        style={workspaceThemeStyle}
        aria-label="研究工作区导航"
      >
        {!isMinimalWorkspace ? (
          <div className={styles.headerRow}>
            <div className={styles.headingStack}>
              <div className={styles.eyebrow}>{routeMeta.sectionLabel}</div>
              <div className={styles.titleRow}>
                <h2 className={styles.title}>{routeMeta.title}</h2>
              </div>
              <p className={styles.desc}>{routeMeta.description}</p>
            </div>
          </div>
        ) : null}

        <div className={styles.laneRow}>
          {primaryLanes.map((lane) => {
            const isActive = routeMeta.activeLane === lane.path;
            return (
              <button
                key={lane.path}
                type="button"
                className={`${styles.laneButton} ${isActive ? styles.laneButtonActive : ''}`}
                onClick={() => history.push(lane.path)}
              >
                <span className={styles.laneLabel}>{lane.label}</span>
                {!isMinimalWorkspace ? <span className={styles.lanePath}>{lane.path}</span> : null}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ResearchWorkspaceGuide;
